/**
 * Tela de um representante no mês (itens 3 e 3.1 do pedido): faturamento, % da
 * meta, supermeta, projeção, a visão por marca e TODAS as notas que formam os
 * números (é aqui que se confere de onde veio cada real). Um painel por vez:
 * Aliança (marcas do Winthor) ou Bauny (SAP).
 *
 * Lê: dados do mês (dados/usar-dados-do-mes.ts) e os cadastros.
 * Monta os números com: dominio/painel.ts (montarPainel + montarDetalheRepresentante).
 */
import { useMemo } from 'react';
import { Avatar } from '../componentes/Avatar.tsx';
import { Aviso, ErroNaTela, Indicador, SeletorMes, TrocaDePainel } from '../componentes/basicos.tsx';
import { usarDadosDoMes } from '../dados/usar-dados-do-mes.ts';
import { juntarFeriados } from '../dominio/feriados.ts';
import { codigosDoRepresentanteNoMes } from '../dominio/identificacao.ts';
import { montarDetalheRepresentante, montarPainel, type EntradaPainel } from '../dominio/painel.ts';
import { ordenar } from './montar-graficos.ts';
import type { Cadastros, Fonte, Mes } from '../dominio/tipos.ts';
import { formatarData, formatarMoeda, formatarPorcentagem, NOME_DO_PAINEL, NOME_DO_SISTEMA, nomeDoMes } from '../formatacao.ts';
import { irPara, linkPara } from '../rotas.ts';

interface Props {
  id: number;
  mes: Mes;
  /** winthor = Aliança; sap-bauny = Bauny. */
  fonte: Fonte;
  cadastros: Cadastros;
}

export function Representante({ id, mes, fonte, cadastros }: Props) {
  const { dados, carregando, erro } = usarDadosDoMes(mes, fonte);
  const representante = cadastros.representantes.find((r) => r.id === id) ?? null;

  const resultado = useMemo(() => {
    if (!dados) return null;
    const entrada: EntradaPainel = { mes: dados.mes, fonte, vendas: dados.vendas, cadastros, metas: dados.metas, feriados: juntarFeriados(cadastros.feriados), carteiras: dados.carteiras };
    const painel = montarPainel(entrada);
    const detalhe = montarDetalheRepresentante(entrada, id, painel);
    const posicao = ordenar(painel.linhas, 'atingimento').findIndex((l) => l.representante.id === id) + 1;
    return { painel, detalhe, posicao };
  }, [dados, cadastros, id, fonte]);

  if (!representante) {
    return (
      <div className="pagina">
        <Aviso tipo="erro">Representante não encontrado. <a href={linkPara({ tela: 'painel', mes, fonte })}>Voltar ao painel</a></Aviso>
      </div>
    );
  }

  const linha = resultado?.detalhe.linha ?? null;
  const vendas = resultado?.detalhe.vendas ?? [];
  const totalFaturado = vendas.reduce((soma, v) => soma + v.valorFaturado, 0);
  const totalDevolucao = vendas.reduce((soma, v) => soma + v.valorDevolucao, 0);
  const totalLiquido = vendas.reduce((soma, v) => soma + v.valorLiquido, 0);
  const sistema = NOME_DO_SISTEMA[fonte];
  // Só os códigos que são dele no mês aberto (depois de uma troca, o 42 da Fabricia é da Tatielly).
  const codigos = codigosDoRepresentanteNoMes(cadastros.codigos, id, mes)
    .filter((c) => c.fonte === fonte)
    .map((c) => c.codigo);
  const comoAparece = fonte === 'winthor' ? 'Código no Winthor' : 'Nome no SAP';
  const porMarca = resultado?.painel.origem.modo === 'por-marca';
  const nomeDaMarca = new Map(cadastros.marcas.map((m) => [m.id, m.nome]));

  return (
    <div className="pagina">
      <div className="pagina-titulo">
        <div>
          <a href={linkPara({ tela: 'painel', mes, fonte })}>← Painel {NOME_DO_PAINEL[fonte]}</a>
          <h1 className="nome-com-foto">
            <Avatar nome={representante.nome} foto={representante.foto} tamanho={56} />
            {representante.nome}
          </h1>
          <p>
            {codigos.length > 0 ? `${comoAparece}: ${codigos.join(', ')}` : `Sem ${comoAparece.toLowerCase()} em ${nomeDoMes(mes)}`}
            {!representante.entraNoRanking ? ' · fora do ranking e das metas' : ''}
          </p>
        </div>
        <SeletorMes mes={mes} aoMudar={(novo) => irPara({ tela: 'representante', id, mes: novo, fonte })} />
      </div>
      <TrocaDePainel ativo={fonte} link={(outra) => linkPara({ tela: 'representante', id, mes, fonte: outra })} />

      {erro ? <ErroNaTela erro={erro} /> : null}
      {codigos.length === 0 ? (
        <Aviso tipo="alerta">
          Sem o {comoAparece.toLowerCase()}, as vendas deste representante no {sistema} não aparecem.{' '}
          <a href={linkPara({ tela: 'cadastros', mes })}>Cadastrar</a>
        </Aviso>
      ) : null}

      {resultado ? (
        <div className={carregando ? 'pilha carregando' : 'pilha'}>
          <div className="indicadores">
            <Indicador
              principal
              rotulo={`Faturamento líquido · ${nomeDoMes(mes)}`}
              valor={formatarMoeda(linha?.liquido ?? totalLiquido)}
              detalhe={`${linha?.notas ?? vendas.length} notas · ${linha?.clientes ?? 0} clientes atendidos`}
            />
            <Indicador
              rotulo="Meta atingida"
              valor={linha && linha.meta > 0 ? formatarPorcentagem(linha.atingimento) : 'sem meta'}
              detalhe={linha && linha.meta > 0 ? `meta ${formatarMoeda(linha.meta)} · supermeta ${formatarMoeda(linha.supermeta)}` : undefined}
            />
            <Indicador
              rotulo="Falta para a meta"
              valor={linha && linha.meta > 0 ? formatarMoeda(linha.faltaParaMeta) : '—'}
              detalhe={
                linha && linha.meta > 0 && resultado.painel.ritmo !== null
                  ? `ritmo esperado hoje: ${formatarPorcentagem(resultado.painel.ritmo, 0)} da meta`
                  : undefined
              }
            />
            <Indicador
              rotulo="Projeção do mês"
              valor={linha?.projecao == null ? '—' : formatarMoeda(linha.projecao)}
              detalhe={linha?.atingimentoProjetado == null ? undefined : `${formatarPorcentagem(linha.atingimentoProjetado)} da meta no ritmo atual`}
            />
            {resultado.painel.carteiraUsada ? (
              <Indicador
                rotulo="Carteira atendida"
                valor={formatarPorcentagem(linha?.carteira ?? null)}
                detalhe={
                  linha?.clientesNaCarteira
                    ? `${linha.clientes} de ${linha.clientesNaCarteira} clientes da carteira compraram`
                    : 'sem clientes na carteira'
                }
              />
            ) : null}
            <Indicador
              rotulo="Participação na equipe"
              valor={formatarPorcentagem(linha?.participacao ?? null)}
              detalhe={resultado.posicao > 0 ? `${resultado.posicao}º lugar na meta, de ${resultado.painel.linhas.length}` : undefined}
            />
          </div>

          <section className="cartao pilha">
            <div>
              <h2>Por marca</h2>
              <p className="subtitulo">
                Meta de {nomeDoMes(mes)} por marca {fonte === 'winthor' ? 'do Winthor' : 'da Bauny (SAP)'} e quanto já foi feito em cada uma.
              </p>
            </div>
            {!resultado.detalhe.temMarcaNosDados ? (
              <Aviso tipo="info">
                Neste mês só foi importado o arquivo geral do Winthor (todas as marcas juntas), então o realizado aparece só no total. Importando um
                arquivo por marca, esta tabela se completa sozinha.
              </Aviso>
            ) : null}
            <div className="tabela-rolavel">
              <table className="tabela">
                <thead>
                  <tr>
                    <th>Marca</th>
                    <th className="numero">Meta</th>
                    <th className="numero">Supermeta</th>
                    <th className="numero">Realizado</th>
                    <th className="numero">% da meta</th>
                    <th className="numero">Projeção</th>
                  </tr>
                </thead>
                <tbody>
                  {resultado.detalhe.porMarca.map((item) => (
                    <tr key={item.marca.id}>
                      <td>{item.marca.nome}</td>
                      <td className="numero">{formatarMoeda(item.meta)}</td>
                      <td className="numero">{formatarMoeda(item.supermeta)}</td>
                      <td className="numero">{item.realizado === null ? <span className="texto-apagado" title={item.motivoSemRealizado ?? ''}>—</span> : formatarMoeda(item.realizado)}</td>
                      <td className="numero">{formatarPorcentagem(item.atingimento)}</td>
                      <td className="numero">{item.projecao === null ? '—' : formatarMoeda(item.projecao)}</td>
                    </tr>
                  ))}
                </tbody>
                {linha ? (
                  <tfoot>
                    <tr>
                      <td>{fonte === 'winthor' ? 'Total das marcas do Winthor' : 'Total Bauny'}</td>
                      <td className="numero">{formatarMoeda(linha.meta)}</td>
                      <td className="numero">{formatarMoeda(linha.supermeta)}</td>
                      <td className="numero">{formatarMoeda(linha.liquido)}</td>
                      <td className="numero">{formatarPorcentagem(linha.atingimento)}</td>
                      <td className="numero">{linha.projecao === null ? '—' : formatarMoeda(linha.projecao)}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
            <p className="texto-apagado">
              {fonte === 'winthor' ? (
                <>
                  A Bauny tem o painel dela: <a href={linkPara({ tela: 'representante', id, mes, fonte: 'sap-bauny' })}>ver a Bauny deste representante</a>.
                </>
              ) : (
                <>
                  As marcas do Winthor ficam no painel da Aliança:{' '}
                  <a href={linkPara({ tela: 'representante', id, mes, fonte: 'winthor' })}>ver a Aliança deste representante</a>.
                </>
              )}
            </p>
          </section>

          <section className="cartao pilha">
            <div>
              <h2>Notas do mês</h2>
              <p className="subtitulo">
                Todas as notas do {sistema} que formam os números acima ({vendas.length} linhas
                {porMarca ? '; uma nota com várias marcas aparece uma vez por marca' : ''}).
              </p>
            </div>
            <div className="tabela-rolavel">
              <table className="tabela">
                <thead>
                  <tr>
                    <th>Faturada em</th>
                    <th>NF</th>
                    {porMarca ? <th>Marca</th> : null}
                    <th>Cliente</th>
                    <th>Cidade</th>
                    <th className="numero">Faturado</th>
                    <th className="numero">Devolução</th>
                    <th className="numero">Líquido</th>
                  </tr>
                </thead>
                <tbody>
                  {vendas.map((venda) => (
                    <tr key={`${venda.chave}-${venda.marcaId ?? 'geral'}`}>
                      <td>{formatarData(venda.data)}</td>
                      <td>{venda.nf}</td>
                      {porMarca ? <td>{venda.marcaId == null ? '—' : (nomeDaMarca.get(venda.marcaId) ?? '?')}</td> : null}
                      <td>{venda.cliente}</td>
                      <td>{venda.cidade}</td>
                      <td className="numero">{formatarMoeda(venda.valorFaturado)}</td>
                      <td className="numero">{venda.valorDevolucao > 0 ? formatarMoeda(venda.valorDevolucao) : '—'}</td>
                      <td className="numero">{formatarMoeda(venda.valorLiquido)}</td>
                    </tr>
                  ))}
                  {vendas.length === 0 ? (
                    <tr>
                      <td colSpan={porMarca ? 8 : 7} className="texto-apagado">
                        Nenhuma nota neste mês.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
                {vendas.length > 0 ? (
                  <tfoot>
                    <tr>
                      <td colSpan={porMarca ? 5 : 4}>Total</td>
                      <td className="numero">{formatarMoeda(totalFaturado)}</td>
                      <td className="numero">{formatarMoeda(totalDevolucao)}</td>
                      <td className="numero">{formatarMoeda(totalLiquido)}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </section>
        </div>
      ) : carregando ? (
        <p className="texto-apagado">Carregando…</p>
      ) : null}
    </div>
  );
}
